import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Ultra Colourful & Dynamic FlyToCart Component
 * Features: Multi-color trail, 3D spin, colorful confetti explosion & RGB toast badge
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying item with multi-color trail, 3D spin, and confetti burst.
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    const midX = startPoint.x + (endPoint.x - startPoint.x) * 0.5;
    const midY = Math.min(startPoint.y, endPoint.y) - 120;

    await yieldToMainThread();

    // 1. Arc Flight Animation with Multi-color Glow & 3D Spin
    const flyAnimation = this.animate(
      [
        {
          transform: `translate3d(${startPoint.x}px, ${startPoint.y}px, 0) scale(1) rotate(0deg)`,
          opacity: 1,
          boxShadow: '0 0 20px #ff007f, 0 0 30px #7928ca',
        },
        {
          transform: `translate3d(${midX}px, ${midY}px, 0) scale(0.65) rotate(180deg)`,
          opacity: 0.95,
          boxShadow: '0 0 25px #00dfd8, 0 0 40px #ff007f',
        },
        {
          transform: `translate3d(${endPoint.x}px, ${endPoint.y}px, 0) scale(0.1) rotate(360deg)`,
          opacity: 0.1,
          boxShadow: '0 0 0px transparent',
        },
      ],
      {
        duration: 800,
        easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        fill: 'forwards',
      }
    );

    await flyAnimation.finished;

    // 2. Dynamic Bounce Effect on Cart Icon
    if (this.destination) {
      this.destination.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.4) rotate(-10deg)', filter: 'drop-shadow(0 0 12px #ff007f)' },
          { transform: 'scale(0.85) rotate(10deg)' },
          { transform: 'scale(1)', filter: 'none' },
        ],
        {
          duration: 500,
          easing: 'ease-out',
        }
      );
    }

    // 3. Spawns Rainbow Confetti & Multi-Color Badge
    this.#createColorfulConfetti(endPoint);
    this.#showColorfulToast(endPoint);

    this.remove();
  };

  /**
   * Spawns vibrant 14-point multi-color confetti burst
   * @param {{x: number, y: number}} point
   */
  #createColorfulConfetti = (point) => {
    const rainbowColors = ['#ff0055', '#ff5000', '#ffcc00', '#00e676', '#00b0ff', '#d500f9', '#ff007f'];
    for (let i = 0; i < 14; i++) {
      const confetti = document.createElement('div');
      const size = Math.random() * 8 + 5;
      const angle = (i * 25.7 * Math.PI) / 180;
      const distance = Math.random() * 45 + 25;
      const isSquare = i % 2 === 0;

      Object.assign(confetti.style, {
        position: 'fixed',
        top: `${point.y}px`,
        left: `${point.x}px`,
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: rainbowColors[i % rainbowColors.length],
        borderRadius: isSquare ? '2px' : '50%',
        zIndex: '999999',
        pointerEvents: 'none',
        boxShadow: `0 0 8px ${rainbowColors[i % rainbowColors.length]}`,
      });

      document.body.appendChild(confetti);

      confetti.animate(
        [
          { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
          {
            transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(0) rotate(360deg)`,
            opacity: 0,
          },
        ],
        {
          duration: 700,
          easing: 'cubic-bezier(0.1, 0.8, 0.3, 1)',
        }
      ).onfinish = () => confetti.remove();
    }
  };

  /**
   * Displays vibrant gradient RGB toast badge
   * @param {{x: number, y: number}} point
   */
  #showColorfulToast = (point) => {
    const toast = document.createElement('div');
    toast.textContent = '🎉 Added to Cart!';
    Object.assign(toast.style, {
      position: 'fixed',
      top: `${point.y + 24}px`,
      left: `${point.x - 45}px`,
      background: 'linear-gradient(135deg, #ff007f, #7928ca, #00dfd8)',
      color: '#ffffff',
      padding: '5px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: '900',
      zIndex: '999999',
      boxShadow: '0 4px 15px rgba(255, 0, 127, 0.5)',
      pointerEvents: 'none',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    });

    document.body.appendChild(toast);

    toast.animate(
      [
        { opacity: 0, transform: 'translateY(12px) scale(0.7)' },
        { opacity: 1, transform: 'translateY(0) scale(1.1)', offset: 0.2 },
        { opacity: 1, transform: 'translateY(-10px) scale(1)', offset: 0.8 },
        { opacity: 0, transform: 'translateY(-22px) scale(0.6)' },
      ],
      { duration: 1300, easing: 'ease-in-out' }
    ).onfinish = () => toast.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}